import { useEffect, useState } from "react";
import BottomSheet from "components/common/BottomSheet";
import CommonModal from "components/common/CommonModal";
import { useSnackbar } from "contexts/SnackbarContext";
import {
    createBucketList,
    updateBucketList,
    achieveBucketList,
    mergeCategoryOptions,
    getCategoryLabel,
} from "api/bucketList";
import BucketImageGrid from "components/bucketlist/BucketImageGrid";

const CONTENT_MAX = 20;
const JOURNAL_MAX = 5000;
// 프로필 노출은 최대 3개(BE 노출 규칙)에 맞춰 공개 항목도 3개로 제한
const PUBLIC_MAX = 3;

const todayStr = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

// 추가/수정 공통 폼. 기본은 목표·카테고리·공개.
// 완료 섹션(달성일·기록)은 showCompletion일 때만 초기화한다.
const buildForm = (item, showCompletion) => ({
    content: item?.content ?? "",
    category: item?.category ?? "",
    is_public: item?.is_public ?? false,
    date: showCompletion ? item?.achieved_at || todayStr() : "",
    journal: item?.journal ?? "",
});

/**
 * 버킷리스트 추가/수정/완료 단일 BottomSheet.
 * - 항상: 목표·카테고리·공개
 * - 완료 처리 중(completing)이거나 이미 달성한 항목이면: 달성일·사진·기록이 추가로 노출
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {object|null} props.item 대상(없으면 생성). 완료 섹션은 이 item으로 파생
 * @param {boolean} [props.completing] 미달성 항목을 완료 처리하는 중 (달성 섹션 노출·저장 시 달성)
 * @param {function} props.onSaved 저장 성공 콜백
 * @param {function} [props.onChanged] 사진 변경 후 목록 새로고침(이미지 그리드용)
 * @param {Array} [props.publicItems] 편집 대상 제외 현재 공개 항목 목록(3개 제한 판정 + 스왑 픽커)
 */
const BucketListSheet = ({
    isOpen,
    onClose,
    item = null,
    completing: completingProp = false,
    onSaved,
    onChanged,
    publicItems = [],
}) => {
    const { showError, showSuccess } = useSnackbar();
    // 완료 처리 여부는 내부 상태 — 수정 시트에서 '달성하기'로 전환할 수 있다.
    const [completing, setCompleting] = useState(completingProp);
    const isCreate = !item;
    const isCompleting = completing && !item?.is_achieved; // 최초 달성 처리
    // 완료 처리 중에는 목표/카테고리/공개를 숨기고 달성일/사진/기록만 보여준다.
    // 달성한 항목 수정 시에는 둘 다 보여준다.
    const showBase = !isCompleting; // 목표/카테고리/공개
    const showCompletion = !!item && (item.is_achieved || completing); // 달성일/사진/기록

    const [form, setForm] = useState(buildForm(item, completingProp));
    const [isSaving, setIsSaving] = useState(false);
    // 공개 3개 초과 시 기존 공개 항목 하나를 대신 해제(스왑) — 반영은 저장 시점
    const [swapPickerOpen, setSwapPickerOpen] = useState(false);
    const [swapSelected, setSwapSelected] = useState(null); // 픽커 내 라디오 선택
    const [swapOutId, setSwapOutId] = useState(null); // 저장 시 공개 해제할 항목

    const publicCount = publicItems.length;
    const overLimit = publicCount >= PUBLIC_MAX;
    const swapOutItem = publicItems.find((it) => it.id === swapOutId) || null;

    // 현재 항목의 카테고리가 서버 enum 변경으로 FE 기본 목록에 없을 수 있어 옵션에 합류시킨다
    const categoryOptions = mergeCategoryOptions([item?.category]);

    // 시트가 열릴 때 대상 기준으로 폼·모드·스왑 상태 초기화 (id 기준 — 사진 새로고침엔 리셋 안 되도록)
    useEffect(() => {
        if (!isOpen) return;
        setCompleting(completingProp);
        const sc = !!item && (item?.is_achieved || completingProp);
        setForm(buildForm(item, sc));
        setSwapOutId(null);
        setSwapPickerOpen(false);
        setSwapSelected(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, item?.id]);

    // '달성하기' — 수정 시트에서 완료 처리로 전환(달성일/사진/기록 노출)
    const startCompleting = () => {
        setCompleting(true);
        setForm((prev) => ({
            ...prev,
            date: item?.achieved_at || todayStr(),
            journal: item?.journal ?? "",
        }));
    };

    const updateField = (key, value) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    // 공개는 최대 3개. 초과 시 차단하지 않고 스왑 픽커로 기존 공개 하나를 대신 해제한다.
    const handlePublicToggle = (checked) => {
        if (!checked) {
            updateField("is_public", false);
            setSwapOutId(null); // 끄면 예약된 스왑도 취소
            return;
        }
        // 켜기: 이미 3개 공개면 스왑 픽커(단, 이미 스왑 예약돼 있으면 그대로 켜기)
        if (overLimit && !swapOutId) {
            setSwapSelected(null);
            setSwapPickerOpen(true);
            return;
        }
        updateField("is_public", true);
    };

    // 스왑 확정 — 선택한 항목을 '해제 예정'으로 두고 현재 항목을 공개로 (반영은 저장 시)
    const confirmSwap = () => {
        if (!swapSelected) return;
        setSwapOutId(swapSelected);
        updateField("is_public", true);
        setSwapPickerOpen(false);
    };

    const handleSubmit = async () => {
        if (isSaving) return;

        const content = form.content.trim();
        // 목표/카테고리/공개는 showBase일 때만 검증
        if (showBase) {
            if (!content) {
                showError("목표를 입력해주세요");
                return;
            }
            if (content.length > CONTENT_MAX) {
                showError(`목표는 ${CONTENT_MAX}자 이하로 입력해주세요`);
                return;
            }
            // 스왑 예약 없이 한도 초과로 공개하려는 경우 방어(정상 흐름에선 토글이 픽커를 띄움)
            if (form.is_public && overLimit && !swapOutId) {
                showError(`프로필 공개는 최대 ${PUBLIC_MAX}개까지 가능해요`);
                return;
            }
        }
        if (showCompletion) {
            if (!form.date) {
                showError("달성일을 선택해주세요");
                return;
            }
            if (form.journal.length > JOURNAL_MAX) {
                showError(`기록은 ${JOURNAL_MAX}자 이하로 입력해주세요`);
                return;
            }
        }

        // 화면에 보이는 필드만 저장한다.
        const patch = {};
        if (showBase) {
            patch.content = content;
            patch.category = form.category || null;
            patch.is_public = form.is_public;
        }
        if (showCompletion) {
            patch.journal = form.journal.trim() ? form.journal : null;
        }

        try {
            setIsSaving(true);
            if (isCreate) {
                await createBucketList(patch);
            } else {
                if (Object.keys(patch).length) {
                    await updateBucketList(item.id, patch);
                }
                // 달성일 지정/변경(= 달성 처리). 이미 달성이어도 날짜만 갱신.
                if (showCompletion) {
                    await achieveBucketList(item.id, form.date);
                }
            }
            // 스왑: 이 항목이 공개로 저장되면, 선택한 기존 공개 항목을 해제한다.
            if (form.is_public && swapOutId) {
                await updateBucketList(swapOutId, { is_public: false });
            }
            showSuccess(
                isCreate
                    ? "버킷리스트를 추가했어요"
                    : isCompleting
                    ? "달성을 축하해요! 🎉"
                    : "저장했어요"
            );
            onSaved?.();
            onClose();
        } catch (err) {
            const data = err?.response?.data;
            const msg =
                data?.content?.[0] ||
                data?.detail ||
                data?.error ||
                (isCreate ? "추가에 실패했어요" : "저장에 실패했어요");
            showError(typeof msg === "string" ? msg : "저장에 실패했어요");
        } finally {
            setIsSaving(false);
        }
    };

    const title = isCreate
        ? "새 버킷리스트 추가"
        : isCompleting
        ? "버킷리스트 완료"
        : "버킷리스트 수정";
    const submitLabel = isCreate ? "추가하기" : isCompleting ? "완료" : "저장";

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            height="auto"
            className="bucket-sheet"
        >
            <div className="bucket-form">
                {isCompleting && (
                    <p className="bucket-record__celebrate">
                        🎉 <b>{item.content}</b> 달성을 축하해요!
                        <br />
                        달성일과 사진·기록을 남겨 순간을 저장해보세요.
                    </p>
                )}

                {showBase && (
                  <>
                <div className="bucket-form__row">
                    <label className="bucket-form__label" htmlFor="bucket-content">
                        목표 입력
                    </label>
                    <input
                        id="bucket-content"
                        className="bucket-form__input"
                        type="text"
                        maxLength={CONTENT_MAX}
                        placeholder="어떤 꿈을 이루고 싶나요?"
                        value={form.content}
                        onChange={(e) => updateField("content", e.target.value)}
                    />
                    <span className="bucket-form__counter">
                        {form.content.length}/{CONTENT_MAX}
                    </span>
                </div>

                <div className="bucket-form__row">
                    <span className="bucket-form__label">카테고리 선택</span>
                    <div className="bucket-form__chips" aria-label="카테고리 선택">
                        {categoryOptions.map((c) => {
                            const selected = form.category === c.code;
                            return (
                                <button
                                    key={c.code}
                                    type="button"
                                    aria-pressed={selected}
                                    className={`bucket-chip${
                                        selected ? " bucket-chip--active" : ""
                                    }`}
                                    // 선택된 칩을 다시 누르면 해제(카테고리는 선택사항)
                                    onClick={() =>
                                        updateField(
                                            "category",
                                            selected ? "" : c.code
                                        )
                                    }
                                >
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <label className="bucket-form__public">
                    <span className="bucket-form__public-text">
                        <b>프로필에 공개</b>
                        <small>
                            공개한 항목은 커뮤니티 프로필에 노출돼요 (최대{" "}
                            {PUBLIC_MAX}개)
                        </small>
                        {swapOutItem && (
                            <span
                                className="bucket-form__swap-hint"
                                // 힌트 클릭이 라벨 토글을 트리거하지 않도록
                                onClick={(e) => e.preventDefault()}
                            >
                                저장하면 <b>{swapOutItem.content}</b>의 공개가
                                해제되고 이 항목이 공개돼요.
                            </span>
                        )}
                    </span>
                    <span className="inline-visibility-toggle__switch">
                        <input
                            type="checkbox"
                            checked={form.is_public}
                            onChange={(e) => handlePublicToggle(e.target.checked)}
                        />
                        <span className="inline-visibility-toggle__slider" />
                    </span>
                </label>
                  </>
                )}

                {/* 완료 섹션 — 달성일·사진·기록 (완료 처리 중이거나 달성 항목 수정) */}
                {showCompletion && (
                    <>
                        {showBase && <div className="bucket-form__divider" />}
                        <div className="bucket-form__row">
                            <label
                                className="bucket-form__label"
                                htmlFor="bucket-date"
                            >
                                달성일
                            </label>
                            <input
                                id="bucket-date"
                                className="bucket-form__input bucket-form__date-input"
                                type="date"
                                value={form.date}
                                onChange={(e) => updateField("date", e.target.value)}
                                // 아이콘뿐 아니라 날짜 영역 아무 곳을 눌러도 달력이 열리도록
                                onClick={(e) => {
                                    try {
                                        e.currentTarget.showPicker?.();
                                    } catch {
                                        /* showPicker 미지원/제약 시 무시 */
                                    }
                                }}
                            />
                        </div>

                        <BucketImageGrid item={item} onChanged={onChanged} />

                        <div className="bucket-form__row">
                            <label
                                className="bucket-form__label"
                                htmlFor="bucket-journal"
                            >
                                기록
                            </label>
                            <textarea
                                id="bucket-journal"
                                className="bucket-form__textarea bucket-form__textarea--journal"
                                rows={5}
                                maxLength={JOURNAL_MAX}
                                placeholder="달성 과정·소감 등 긴 기록을 자유롭게 남겨보세요"
                                value={form.journal}
                                onChange={(e) => updateField("journal", e.target.value)}
                                // 결제 SDK(innopay)가 document에서 Enter를 preventDefault해 줄바꿈이 막힘.
                                // document까지 전파를 막아 textarea 줄바꿈을 복구한다.
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") e.stopPropagation();
                                }}
                            />
                            <span className="bucket-form__counter">
                                {form.journal.length}/{JOURNAL_MAX}
                            </span>
                        </div>
                    </>
                )}

                {/* 취소는 X·배경 클릭으로 대체. 미달성 수정 시에는 그 자리에 '달성하기' */}
                <div className="bucket-form__actions">
                    {item && !item.is_achieved && !completing && (
                        <button
                            type="button"
                            className="btn btn-fit bucket-form__achieve-btn"
                            onClick={startCompleting}
                            disabled={isSaving}
                        >
                            달성하기
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-purple500 btn-fit"
                        onClick={handleSubmit}
                        disabled={isSaving}
                    >
                        {isSaving ? "저장 중..." : submitLabel}
                    </button>
                </div>
            </div>

            {/* 공개 3개 초과 시 — 내릴 항목 선택(스왑). 반영은 이 시트 저장 시 함께 */}
            <CommonModal
                isOpen={swapPickerOpen}
                onRequestClose={() => setSwapPickerOpen(false)}
                className="bucket-swap-modal"
            >
                <div className="bucket-modal-body">
                    <p className="bucket-modal-body__title">
                        공개는 최대 {PUBLIC_MAX}개예요
                    </p>
                    <p className="bucket-modal-body__desc">
                        새로 공개하려면 지금 공개 중인 항목 하나를 내려야 해요.
                    </p>
                    <ul className="bucket-swap-list">
                        {publicItems.map((it) => {
                            const label = getCategoryLabel(it.category);
                            return (
                                <li key={it.id}>
                                    <label className="bucket-swap-option">
                                        <input
                                            type="radio"
                                            name="swap-out"
                                            checked={swapSelected === it.id}
                                            onChange={() => setSwapSelected(it.id)}
                                        />
                                        <span className="bucket-swap-option__text">
                                            {label && (
                                                <span className="bucket-swap-option__cat">
                                                    {label}
                                                </span>
                                            )}
                                            {it.content}
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="bucket-modal-actions">
                        <button
                            type="button"
                            className="btn btn-gray200 btn-fit"
                            onClick={() => setSwapPickerOpen(false)}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="btn btn-purple500 btn-fit"
                            onClick={confirmSwap}
                            disabled={!swapSelected}
                        >
                            바꾸기
                        </button>
                    </div>
                </div>
            </CommonModal>
        </BottomSheet>
    );
};

export default BucketListSheet;
